import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ADMIN_ROLES, ALL_ROLES, CLIENT_ROLES, OPERATIONAL_ROLES, PARAMETER_ROLES, roleAllowed } from './lib/accessProfiles';
import { SidebarPermissionsProvider } from './contexts/SidebarPermissionsContext';

import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import AdminPanel from './pages/AdminPanel';
import Upload from './pages/Upload';
import ApiSends from './pages/ApiSends';
import CreateUser from './pages/CreateUser';
import UserManagement from './pages/UserManagement';
import OrganizationalSettings from './pages/OrganizationalSettings';
import SidebarPermissions from './pages/SidebarPermissions';
import ApiConfigs from './pages/ApiConfigs';
import MaxparPolicyQuery from './pages/MaxparPolicyQuery';
import Clients from './pages/Clients';
import GuaibimAssistUpload from './pages/GuaibimAssistUpload';
import ProductCustomers from './pages/ProductCustomers';
import BillingRules from './pages/BillingRules';
import ProductProcessings from './pages/ProductProcessings';
import EmailIntake from './pages/EmailIntake';
import './App.css';

// Componente para rotas protegidas
/**
 * Sends a signed-in user away from a route their profile cannot open.
 */
function ProtectedRoute({ children, roles = ALL_ROLES }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!roleAllowed(user.role, roles)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Componente principal da aplicação
function AppContent() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#f1f5f9]">
      <main className="min-h-screen">
        <Routes>
          <Route path="/login" element={
            user ? <Navigate to="/dashboard" replace /> : <Login />
          } />
          <Route path="/register" element={
            user ? <Navigate to="/dashboard" replace /> : <Register />
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute roles={ALL_ROLES}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/clients" element={
            <ProtectedRoute roles={CLIENT_ROLES}>
              <Clients />
            </ProtectedRoute>
          } />
          <Route path="/clients/policies" element={
            <ProtectedRoute roles={OPERATIONAL_ROLES}>
              <ProductCustomers />
            </ProtectedRoute>
          } />
          <Route path="/clients/billing" element={
            <ProtectedRoute roles={PARAMETER_ROLES}>
              <BillingRules />
            </ProtectedRoute>
          } />
          <Route path="/clients/processings" element={
            <ProtectedRoute roles={OPERATIONAL_ROLES}>
              <ProductProcessings />
            </ProtectedRoute>
          } />
          <Route path="/clients/guaibim-assist" element={
            <ProtectedRoute roles={OPERATIONAL_ROLES}>
              <GuaibimAssistUpload />
            </ProtectedRoute>
          } />
          <Route path="/upload" element={
            <ProtectedRoute roles={OPERATIONAL_ROLES}>
              <Upload />
            </ProtectedRoute>
          } />
          <Route path="/api-sends" element={
            <ProtectedRoute roles={OPERATIONAL_ROLES}>
              <ApiSends />
            </ProtectedRoute>
          } />
          <Route path="/maxpar-query" element={
            <ProtectedRoute roles={OPERATIONAL_ROLES}>
              <MaxparPolicyQuery />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <AdminPanel />
            </ProtectedRoute>
          } />
          <Route path="/users/create" element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <CreateUser />
            </ProtectedRoute>
          } />
          <Route path="/users/edit/:userId" element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <CreateUser />
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="/settings/organizational" element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <OrganizationalSettings />
            </ProtectedRoute>
          } />
          <Route path="/settings/api-configs" element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <ApiConfigs />
            </ProtectedRoute>
          } />
          <Route path="/settings/sidebar-permissions" element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <SidebarPermissions />
            </ProtectedRoute>
          } />
          <Route path="/settings/email-intake" element={
            <ProtectedRoute roles={ADMIN_ROLES}>
              <EmailIntake />
            </ProtectedRoute>
          } />
          <Route path="/" element={
            user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } />
        </Routes>
      </main>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </div>
  );
}

// App principal com providers
function App() {
  return (
    <AuthProvider>
      <SidebarPermissionsProvider>
        <Router>
          <AppContent />
        </Router>
      </SidebarPermissionsProvider>
    </AuthProvider>
  );
}

export default App;
